import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import queryString from "query-string";
import { observer } from "mobx-react"; 
import {
    Box,
    Button,
    Text,
    useToast,
    SimpleGrid,
    // Tag,
    // Avatar,
    // TagLabel
} from "@chakra-ui/react";
import {
    // useUserQuery,
    useUpcomingAppointmentsQuery,
    useCancelConsultMutation,
    usePatientLazyQuery,
} from "../graphql/types-and-hooks";
import LoginModal from "./LoginModal";
import UserDetails from "./UserDetails";
import DoctorCardService from "./DoctorCardService";
import { getAppointmentTypesForClinic } from "../services/AppointmentTypesService";
import { getDoctorsWithFirstAvailableForAptGroupId } from "../managers/doctors";
import type { DoctorWithSlot } from "../types/DoctorWithSlot";
// import { IoIosArrowForward } from "react-icons/io";
import {
    servicebookingFlowStore,
    BookingStep,
} from "../store/ServiceBookingFlowStore";
import ServiceBookingFlowModal from "./ServiceBookingFlowModal";
import { Loading } from "./Loading";
import { doctorsStore } from "../store/DoctorsStore";
import usePlacesAutocomplete from "use-places-autocomplete";
import { patientStore } from "../store/PatientStore";
import { appointmentTypesStore } from '../store/AppointmentTypesStore';
import { logError } from "./logger";
import SearchLocation from "./SearchLocation";
import AppPageSkeleton from "./AppPageSkeleton";
import { fetchAndCacheAppConfig } from "../utils/configManager";
import { DateTime } from "luxon";
import { useAlert } from "./AlertProvider";
import OneCardSkeleton from "./OneCardSkeleton";
import AppointmentCard from "./AppointmentCard";
import AppointmentGroupSelector from "./AppointmentGroupSelector";
import { AppointmentGroup } from "../types/appointmentgroup";
import CardSkeleton from "./CardSkeleton";
import CreateFamilyMember from "./CreateFamilyMember";
import logger from "../utils/logger"; //
import CustomAppointmentSelector from "./CustomAppointmentSelector";
import { sessionManager } from "../managers/SessionManager";
import * as DoctorsService from "../services/DoctorsService";
import { clinicStore } from "../store/ClinicStore";
import { getClinicV2 } from "../services/ClinicService";
// import * as AuthService from "../services/AuthService";
import { useAuth } from '../services/AuthContext';

// Add a consistent default duration
const DEFAULT_DURATION = "15";

// Enhanced type for a doctor with earliest availability data
interface DoctorWithAvailability extends DoctorWithSlot {
    earliest_availability?: {
        date: string;
        time: string;
    };
    doctor_name?: string; // Added to fix the TypeScript error
}

export const AppPage = observer(() => {
    // Wrapped with observer HOC
    // The id from the URL will be used directly as the service_type_id
    const { id } = useParams();
    // eslint-disable-next-line no-restricted-globals
    const parsed = queryString.parse(location.search);

    // Access url query variables
    const aptGroupId = parsed.aptGroupId || "";
    const appId = id || "";
    const toast = useToast();

    const [lat, setLat] = useState<number | null>(null);
    const [long, setLong] = useState<number | null>(null);
    const [config, setConfig] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [hasClassBAuth] = useState<string | null>(null);
    const [doctorsLoading, setDoctorsLoading] = useState(false);
    // eslint-disable-next-line
    const [searchQuery, setSearchQuery] = useState("");

    /* eslint-disable */
    const {
        ready,
        value,
        suggestions: { status, data },
    } = usePlacesAutocomplete({
        requestOptions: {
            componentRestrictions: { country: "au" },
            types: ["(regions)"],
        },
    });
    /* eslint-enable */

    // Get user data first so isLoggedIn is available before it's used
    // eslint-disable-next-line
    const { user: authUser, token } = useAuth();
    const [user, setUser] = useState(authUser);
    const isLoggedIn = !!user;

    const { showAlert } = useAlert();

    useEffect(() => {
        setUser(authUser);
        // eslint-disable-next-line
      }, [authUser]);
      

    // Service booking flow modal state - Now this will be properly tracked
    const isModalOpen = servicebookingFlowStore.isModalOpen;

    const {
        data: upcomingAppointmentsData,
        loading: upcomingAppointmentsLoading,
        error: upcomingAppointmentsError,
        refetch: refetchUpcomingAppointments,
    } = useUpcomingAppointmentsQuery();

    const formattedAppointmentsData = useMemo(() => {
        const appointments = upcomingAppointmentsData?.upcomingAppointments?.filter(
            (appointment) =>
                DateTime.fromISO(`${appointment.date}T${appointment.time}:00`) >
                DateTime.now()
        );

        return appointments || [];
    }, [upcomingAppointmentsData]);

    // eslint-disable-next-line
    const [cancelConsultMutation, { loading: cancelConsultLoading }] =
        useCancelConsultMutation({
            refetchQueries: [
                "User",
                "UpcomingAppointments",
                "AppointmentSlots",
                "Calendar",
            ],
            awaitRefetchQueries: true,
        });

    const renderAppointments = () => {
        if (upcomingAppointmentsLoading) return <Loading height={100} />;
        if (upcomingAppointmentsError) {
            return null;
        }
        if (!formattedAppointmentsData.length) return null;

        return formattedAppointmentsData.map((appointment, index) => {
            const date = DateTime.fromISO(appointment.date);
            const day = date.day;
            const weekday = date.weekdayLong;
            const month = date.monthLong;
            const time = DateTime.fromISO(
                `${appointment.date}T${appointment.time}`
            ).toFormat("hh:mm a");

            return (
                <AppointmentCard
                    key={`${appointment.appointment_id}-${index}`}
                    date={day.toString()}
                    day={weekday}
                    month={month}
                    time={time}
                    doctorName={appointment.doctor_full_name}
                    clinicName={appointment.location_display_name}
                    appointmentType={appointment.appointment_type_name}
                    patientName={`${appointment.first_name} ${appointment.last_name}`}
                    appointmentId={`${appointment.appointment_id}`}
                    onCancel={() => handleCancelAppointment(appointment)}
                />
            );
        });
    };

    const handleCancelAppointment = async (appointment) => {
        try {

            // If we cannot determine the cancellation period we prompt the user to contact the clinic
            if (!appointment?.cancellation_period && !appointment?.cancellation_period == null) {
                // Show alert to user
                showAlert({
                    severity: "warning",
                    title: "Can't Cancel",
                    message: "Can not cancel appointment within cancellation period",
                });
                return;
            }

            //cancelAPpointment API

            const cancellationTime = appointment?.cancellation_period != null
                ? DateTime.now().plus({ hours: parseInt(appointment.cancellation_period) })
                : DateTime.now();

            const appointmentTime = DateTime.fromISO(
                `${appointment.date}T${appointment.time}:00`
            );


            if (cancellationTime > appointmentTime) {
                // Show alert to user
                showAlert({
                    severity: "warning",
                    title: "Can't Cancel",
                    message: "Can not cancel appointment after appointment time",
                });
                return;
            }

            await cancelConsultMutation({
                variables: {
                    input: {
                        clinic_group_crm: appointment.clinic_group_crm,
                        user_fk: appointment.user_fk,
                        location_id: appointment.location_id,
                        appointment_id: appointment.appointment_id,
                    },
                },
            });

            // GTAG EVENT
            (window as any).gtag("event", "service_booking_cancelled", {
                clinic_group_crm: appointment.clinic_group_crm,
                user_fk: appointment.user_fk,
                location_id: appointment.location_id,
            });

            await refetchUpcomingAppointments();
        } catch (error) {
            logError("Error cancelling appointment", error);

            return null;
        }

        return null;
    };

    const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);

    const handleLoginModalClose = useCallback(() => {
        setIsLoginModalOpen(false);

        // If there's a doctor waiting to be booked and user is now logged in
        const selectedDoctor =
            servicebookingFlowStore?.appointmentData?.selectedDoctor;
        const serviceType = servicebookingFlowStore?.appointmentData?.serviceType;

        if (selectedDoctor && serviceType && authUser) {
            // Open the booking modal and start from the first step
            servicebookingFlowStore.setIsModalOpen(true);

            // Let user choose who the appointment is for rather than pre-selecting
            // servicebookingFlowStore.updateAppointmentData("isForUser", true);

            // Start at the first step
            servicebookingFlowStore.goToStep(BookingStep.USER_SELECTION);
            servicebookingFlowStore.setActiveStep(0);
        }
    }, [authUser]);

    // Fetch patient data when user is available
    const [getPatient] = usePatientLazyQuery();

    // Define a method to set the selected doctor and open booking modal - moved below isLoggedIn declaration
    const handleBookDoctor = async (doctor) => {
        // Open modal immediately for user responsiveness
        servicebookingFlowStore.resetFlow();
        servicebookingFlowStore.setServiceBooking(true);
        servicebookingFlowStore.setIsModalOpen(true);
        servicebookingFlowStore.setActiveStep(0);

        // Store the selected doctor immediately (optional: can move after data fetch)
        servicebookingFlowStore.updateAppointmentData("selectedDoctor", doctor);
        
        const clinicData = await getClinicV2(doctor.clinic_group_crm, doctor.user_fk, doctor.location_id, );
        const currentClinic = Array.isArray(clinicData) && clinicData.length > 0 ? clinicData[0] : null;
        if (currentClinic) {
            clinicStore.setCurrentClinic(currentClinic);
        }
        logger.log("Current Clinic", clinicData);

        logger.log("Selected Doctor for Booking", doctor);
        // Fire off background fetches (no need to await before modal opens)
        const fetchData = async () => {
            // Fetch patient data
            if (user && getPatient) {
                try {
                    logger.log({
                        clinic_group_crm: doctor.clinic_group_crm,
                        user_fk: doctor.user_fk,
                        location_id: doctor.location_id,
                        dob: user.dob,
                        first_name: user.first_name,
                        last_name: user.last_name,
                        contact_number: user.phone_number,
                    });
                    const patientData = await getPatient({
                        variables: {
                            input: {
                                clinic_group_crm: doctor.clinic_group_crm,
                                user_fk: doctor.user_fk,
                                location_id: doctor.location_id,
                                dob: user.dob,
                                first_name: user.first_name,
                                last_name: user.last_name,
                                contact_number: user.phone_number,
                            },
                        },
                    });

                    logger.log("patientData", patientData);

                    const patientInfo = {
                        id: patientData?.data?.patient?.patient_id,
                        type: "myself",
                        first_name: user.first_name,
                        last_name: user.last_name,
                        dob: user.dob,
                        contact_number: user.phone_number,
                        is_new_pt: patientData?.data?.patient?.is_new_pt,
                        last_seen_did: patientData?.data?.patient?.last_seen_did,
                    };
                    logger.log("patientInfo1", patientInfo);

                    patientStore.setCurrentPatient(patientInfo);
                    patientStore.setMainLoginPatient(patientInfo);
                } catch (error) {
                    const referenceCode = logError(error, {
                        location: "AppPage: fetchPatientData",
                    });

                    console.error(
                        `Error fetching patient data (Reference Code: ${referenceCode}):`,
                        error
                    );

                    toast({
                        title: "Error",
                        description: "Could not fetch patient information.",
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                    });
                }
            }

            // Fetch and set appointment type
            try {
                const clinicAppointmentTypesData = await getAppointmentTypesForClinic(
                    doctor.clinic_group_crm,
                    doctor.user_fk,
                    doctor.location_id
                );

                logger.log("clinicAppointmentTypesData", clinicAppointmentTypesData);
                logger.log("DoctormatchedAppointmentType", doctor?.matched_apt_type_id);

                const matchedAppointmentType = clinicAppointmentTypesData.find(
                    (appointmentType) =>
                        String(appointmentType?.appointment_type_id) ===
                        String(doctor?.matched_apt_type_id)
                );

                logger.log("matchedAppointmentType", matchedAppointmentType);
                appointmentTypesStore.setSelectedAptType(matchedAppointmentType);
                sessionManager.updateDoctorsForAppointmentType(matchedAppointmentType);
                const clinicInfo = {
                    groupCRM: doctor.clinic_group_crm,
                    userFK: doctor.user_fk,
                    locationId: doctor.location_id,
                };
                const docsForAptData = await DoctorsService.getDoctorsForAptType(
                    clinicInfo,
                    doctor?.matched_apt_type_id
                );

                logger.log("The realtime Single Source Truth docsForAptData", docsForAptData);
                doctorsStore.setSelectedAptTypeDoctors(docsForAptData.doctors);
                doctorsStore.setSelectedAptTypeFirstAvailableTimes(docsForAptData.first_time_slots);
                logger.log("The realtime Single Source Truth firstAvailableTimeData :", doctorsStore.selectedAptTypeFirstAvailableTimes?.[doctor.doctor_id]);
                logger.log("matchedAppointmentType", matchedAppointmentType);
                servicebookingFlowStore.updateAppointmentData(
                    "appointmentType",
                    matchedAppointmentType
                );
                logger.log("Real Time Single Source Truth Doctor", doctor);
            } catch (error) {
                logError("Error loading appointment types", error, {
                    location: "AppPage: handleBookDoctor",
                    doctorId: doctor.doctor_id,
                });
                console.error("Error loading appointment types:", error);
                toast({
                    title: "Error",
                    description: "Could not load appointment type for this doctor.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        };

        // GTAG EVENT
        (window as any).gtag("event", "service_start_booking", {
            clinic_group_crm: doctor.clinic_group_crm,
            user_fk: doctor.user_fk,
            location_id: doctor.location_id,
        });

        // Kick off the async loading
        fetchData();
    };

    // eslint-disable-next-line
    const [isCreateFamilyMemberModalOpen, setIsCreateFamilyMemberModalOpen] =
        useState(false);
    // eslint-disable-next-line
    const [isCreateFamilyMemberOpen, setIsCreateFamilyMemberOpen] =
        useState(false);

    const openCreateFamilyMember = () => setIsCreateFamilyMemberOpen(true);
    const closeCreateFamilyMember = () => setIsCreateFamilyMemberOpen(false);

    const handleModalClose = () => {
        servicebookingFlowStore.setIsModalOpen(false);
        servicebookingFlowStore.resetFlow();
    };

    const [doctors, setDoctors] = useState<DoctorWithAvailability[]>([]);
    const [appointmentGroups, setAppointmentGroups] = useState<
        AppointmentGroup[]
    >([]);
    const [selectedAptGroup, setSelectedAptGroup] = useState<
        AppointmentGroup | undefined
    >(undefined);

    // Fetch app configuration
    useEffect(() => {
        if (!appId) return;

        const load = async () => {
            const config = (await fetchAndCacheAppConfig(appId)) as {
                appointment_groups: {
                    appointment_group_id: string;
                    name: string;
                    is_active: boolean;
                    is_default: boolean;
                }[];
            };
            setConfig(config);
            // console.log(config);
            servicebookingFlowStore.setConfiguration(config);

            setAppointmentGroups(config.appointment_groups);

            // Determine which appointment group to select initially
            let initialAppointmentGroup;

            // First check if aptGroupId from URL exists and is valid
            if (aptGroupId) {
                initialAppointmentGroup = config.appointment_groups.find(
                    (group) => group.appointment_group_id === aptGroupId
                );
            }

            // If no valid aptGroupId from URL, use the default one
            if (!initialAppointmentGroup) {
                initialAppointmentGroup = config.appointment_groups.find(
                    (group) => group.is_default
                );
            }

            setSelectedAptGroup(initialAppointmentGroup);

            const selectedAptGroupId = initialAppointmentGroup.appointment_group_id;
            // Get doctors for the selected appointment group
            const doctors =
                await getDoctorsWithFirstAvailableForAptGroupId(selectedAptGroupId);

            doctorsStore.setSelectedAptTypeDoctors(
                doctors as unknown as DoctorWithAvailability[]
            );
            setDoctors(doctors as unknown as DoctorWithAvailability[]);
            setLoading(false);
        };
        load();
    }, [appId, aptGroupId]); // Add aptGroupId as dependency

    useEffect(() => {
        if (lat !== null && long !== null && selectedAptGroup) {
            (async () => {
                try {
                    // setLoading(true);
                    const doctors = await getDoctorsWithFirstAvailableForAptGroupId(
                        selectedAptGroup.appointment_group_id,
                        lat,
                        long
                    );
                    doctorsStore.setSelectedAptTypeDoctors(
                        doctors as unknown as DoctorWithAvailability[]
                    );
                    setDoctors(doctors as unknown as DoctorWithAvailability[]);
                } catch (error) {
                    logError("Error fetching doctors after location change", error, {
                        location: "AppPage: useEffect",
                        lat,
                        long,
                        aptGroupId: selectedAptGroup.appointment_group_id,
                    });
                    console.error("Error fetching doctors after location change:", error);
                    toast({
                        title: "Error",
                        description: "Could not fetch doctors for this location.",
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                    });
                } finally {
                    setLoading(false);
                }
            })();
        }
        // eslint-disable-next-line
    }, [lat, long, selectedAptGroup]);

    const handleAppointmentGroupChange = async (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const selectedGroupId = e.target.value;
        const selectedGroup = appointmentGroups.find(
            (group) => group.appointment_group_id === selectedGroupId
        );

        if (selectedGroup) {
            setSelectedAptGroup(selectedGroup);
        
            try {
                setDoctorsLoading(true); // start loading
                const doctors = await getDoctorsWithFirstAvailableForAptGroupId(selectedGroup.appointment_group_id);
                doctorsStore.setSelectedAptTypeDoctors(
                    doctors as unknown as DoctorWithAvailability[]
                );
                setDoctors(doctors as unknown as DoctorWithAvailability[]);
            } catch (error) {
                logError("Error fetching doctors for selected group", error, {
                    location: "AppPage: handleAppointmentGroupChange",
                    selectedGroupId,
                });
                console.error("Error fetching doctors for selected group:", error);
                toast({
                    title: "Error",
                    description: "Could not fetch doctors for this appointment group.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
              setDoctorsLoading(false); // stop loading
            }
        }
    };

    const handleUseCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const latitude = position.coords.latitude;
                    const longitude = position.coords.longitude;
                    setLat(latitude);
                    setLong(longitude);
                },
                (error) => {
                    console.error("Error getting location:", error.message);
                    toast({
                        title: "Location Error",
                        description:
                            "Could not get your location. Please check your browser settings.",
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                    });
                }
            );
        } else {
            toast({
                title: "Location Not Supported",
                description: "Geolocation is not supported by your browser.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    // Filter doctors based on search query
    const filteredDoctors = React.useMemo(() => {
        if (!searchQuery.trim()) return doctors;

        const lowerCaseQuery = searchQuery.trim().toLowerCase();

        return doctors.filter((doctor) => {
            const doctorName = doctor?.doctor_name || doctor?.display_name || "";
            return doctorName.toLowerCase().includes(lowerCaseQuery);
        });
    }, [doctors, searchQuery]);

    // Show loading state
    if (loading) {
        return <AppPageSkeleton />;
    }

    // Show error if no config
    if (!config) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="300px"
                flexDirection="column"
            >
                <Text fontSize="lg" color="red.500">
                    Could not load configuration.
                </Text>
                <Button
                    onClick={() => window.location.reload()}
                    colorScheme="red"
                    mt={4}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    const colors = config?.colorScheme || {};
    const isSearchLocationActive = config?.isLocationSearchActive;

    return (
        <Box display="flex" flexDirection="column" minHeight="100vh">
            {config?.header_html && (
                <Box dangerouslySetInnerHTML={{ __html: config?.header_html }} />
            )}

            <Box
                display="flex"
                flexDirection={{ base: "column" }}
                width={{ base: "100%", lg: "85%" }}
                overflowX="hidden"
                p={3}
                gap="5"
                margin="auto"
                mt="5"
            >
                <Box
                    boxShadow="md"
                    p="6"
                    rounded="md"
                    borderWidth={2}
                    display="flex"
                    flexWrap="wrap"
                    gap={4}
                    alignItems="center"
                >
                    <Box
                        display="flex"
                        gap={4}
                        width="100%"
                        flexWrap="nowrap"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent={'space-between'}
                    >
                        {/* Appointment Type Selector - Replaced with component */}
                        {isSearchLocationActive && (<AppointmentGroupSelector
                            appointmentGroups={appointmentGroups}
                            selectedAppointmentGroup={selectedAptGroup?.appointment_group_id}
                            handleAppointmentGroupChange={handleAppointmentGroupChange}
                            appointmentGroupsLoading={false}
                            appointmentGroupsError={""}
                        />)}

                        {!isSearchLocationActive && (<Text fontWeight={'bold'} mt={5}>Select your Appointment type </Text>)}
                        {/* No Login Tag for now */}
                        {/* <Box flex="0 0 auto" width="auto">
                            <Tag
                                onClick={() => {
                                    isLoggedIn ? toggleDrawer() : setIsLoginModalOpen(true);
                                }}
                                p="1"
                                mt={3}
                                size="lg"
                                borderRadius="full"
                                _hover={{ cursor: "pointer" }}
                                display="flex"
                                alignItems="center"
                                gap="2"
                                colorScheme={
                                    hasClassBAuth === "B"
                                        ? "green"
                                        : isLoggedIn
                                            ? "blue"
                                            : colors.color
                                }
                            >
                                <Avatar
                                    size="sm"
                                    mr={2}
                                    bg={
                                        hasClassBAuth === "B"
                                            ? "green"
                                            : isLoggedIn
                                                ? "blue"
                                                : colors.buttonBg
                                    }
                                />
                                <TagLabel
                                    color={
                                        hasClassBAuth === "B"
                                            ? "green"
                                            : isLoggedIn
                                                ? "blue"
                                                : colors.buttonBg
                                    }
                                >
                                    {isLoggedIn ? `Hi ${user.first_name}` : "Login"}
                                </TagLabel>
                                <IoIosArrowForward
                                    color={
                                        hasClassBAuth === "B"
                                            ? "green"
                                            : isLoggedIn
                                                ? "blue"
                                                : colors.buttonBg
                                    }
                                />
                            </Tag>
                        </Box> */}
                        

                    </Box>
                    {!isSearchLocationActive && (
                        <CustomAppointmentSelector
                            appointmentGroups={appointmentGroups.map(group => ({
                                appointment_group_id: group.appointment_group_id,
                                name: group.name,
                                price: group.price || "$0",
                                icon: group.icon || "FiUser",
                            }))}
                            selectedAppointmentGroupId={selectedAptGroup?.appointment_group_id || ""}
                            handleAppointmentGroupChange={(id) => handleAppointmentGroupChange({ target: { value: id } } as React.ChangeEvent<HTMLSelectElement>)}
                        />)}


                    {/* Use the SearchLocation component when needed */}
                    {isSearchLocationActive && (
                        <SearchLocation
                            colors={colors}
                            setSearchQuery={setSearchQuery}
                            handleUseCurrentLocation={handleUseCurrentLocation}
                            setLat={setLat}
                            setLong={setLong}
                        />
                    )}

                    <LoginModal
                        isOpen={isLoginModalOpen}
                        onClose={handleLoginModalClose}
                    />
                    <UserDetails
                        isOpen={isDrawerOpen}
                        onClose={toggleDrawer}
                        onOpenCreateFamilyMember={openCreateFamilyMember}
                        isAuthenticated={isLoggedIn}
                        hasClassBAuth={hasClassBAuth}
                    />
                    <CreateFamilyMember
                        isOpen={isCreateFamilyMemberOpen}
                        onClose={closeCreateFamilyMember}
                    />
                </Box>

                {isLoggedIn &&
                    (upcomingAppointmentsLoading ||
                        formattedAppointmentsData.length > 0) ? (
                    <SimpleGrid columns={[1]} spacing={4} mt={5} width={["100%"]}>
                        <Text fontWeight="bold" fontSize={"xl"}>
                            {" "}
                            Your Upcoming Appointments{" "}
                        </Text>

                        {upcomingAppointmentsLoading ? (
                            <OneCardSkeleton />
                        ) : (
                            renderAppointments()
                        )}
                    </SimpleGrid>
                ) : null}

                <Text fontWeight="bold" fontSize={"xl"} mt={5}>
                    Doctors for {selectedAptGroup?.name || "Standard Consultation"}
                </Text>

                {/* Show loading message if doctors are being loaded */}
                {doctorsLoading ? (
                    <CardSkeleton />
                ) : filteredDoctors.length === 0 ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="200px"
                        flexDirection="column"
                        gap={4}

                    >
                        <Text>No doctors available for this appointment type.</Text>
                        <Button onClick={() => window.location.reload()}>Refresh</Button>
                    </Box>
                ) : (
                    <Box >
                        {filteredDoctors.map((doctor, index) => {
                            const firstSlot = doctor.first_slot || {};
                            const fadate = firstSlot.date || "";
                            const fatime = firstSlot.time || "";

                            return (
                                <DoctorCardService
                                    key={`${doctor.doctor_id}-${index}`}
                                    doctor={doctor}
                                    ddate={{ fadate, fatime }}
                                    duration={DEFAULT_DURATION}
                                    userLat={lat}
                                    userLong={long}
                                    onBookDoctor={() => handleBookDoctor(doctor)}
                                />
                            );
                        })}
                    </Box>
                )}

                {/* ServiceBookingFlowModal component */}
                <ServiceBookingFlowModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                />
            </Box>

            {config?.footer_html && (
                <Box mt={4} dangerouslySetInnerHTML={{ __html: config?.footer_html }} />
            )}
        </Box>
    );
});

export default AppPage;
